import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import EngravingImage from "./components/EngravingImage";
import OperationalMap from "./components/OperationalMap";
import PaywallGate from "./components/PaywallGate";
import { AudioPlayer } from "./components/AudioPlayer";
import "./FrontPage.css";

const API_URL = process.env.REACT_APP_API_URL || "https://api.bearing-intelligence.com";
const HERO_FALLBACK = "/hero-placeholder.jpg";

const str = (v) => (v && typeof v === "object" && "text" in v ? v.text : v == null ? "" : String(v));
const firstSentence = (s) => {
  const t = str(s).trim();
  const m = t.match(/^.*?[.!?](\s|$)/);
  return m ? m[0].trim() : t;
};
const deckOf = (dev) => richText(dev.summary) || firstSentence(dev.assessment) || firstSentence(dev.implications) || "";
const kickerOf = (dev) => [dev.domain, dev.severity].filter(Boolean).join(" · ").toUpperCase();
const isCritical = (dev) => String(dev.severity || "").toUpperCase() === "CRITICAL";
const artQuery = (dev) => (str(dev.map_label) || str(dev.headline) || str(dev.domain) || "geopolitics").slice(0, 80);

// Fetch an engraveable image for a keyword. Returns the art object, or null when
// the provider has no key/result (caller then shows the engraved placeholder).
async function fetchArt(theme) {
  try {
    const r = await fetch(`${API_URL}/api/hero-art?theme=${encodeURIComponent(theme)}`);
    if (!r.ok) return null;
    const a = await r.json();
    return a && a.imageUrl ? a : null;
  } catch (_) { return null; }
}

// Unsplash attribution — required visible credit with links to the photographer
// and to Unsplash (both carrying our utm params).
const Attribution = ({ art, fallback = null }) => {
  if (art && art.imageUrl && art.photographerName && art.photographerUrl) {
    return (
      <>Photo by <a href={art.photographerUrl} target="_blank" rel="noopener noreferrer">{art.photographerName}</a>
        {" on "}
        <a href={art.unsplashUrl || "https://unsplash.com/?utm_source=bearing-daily-brief&utm_medium=referral"} target="_blank" rel="noopener noreferrer">Unsplash</a>
      </>
    );
  }
  if (art && art.credit) return <>{art.credit}</>;
  return fallback;
};

// A fixed-height engraved plate — art (via the #bi-engrave filter) or the
// placeholder. Fixed height means adding it doesn't reflow after the image loads.
const ArtPlate = ({ art, height }) => (
  <div className="p1-artwrap">
    <figure className="p1-plate" style={{ height }}>
      <EngravingImage src={(art && art.imageUrl) || HERO_FALLBACK} alt="" />
    </figure>
    {art && art.imageUrl && <figcaption className="p1-credit"><Attribution art={art} /></figcaption>}
  </div>
);

const KdStory = ({ dev, size = "", showArt = false, art = null, artHeight = 0, onOpen }) => (
  <article className={`p1-story ${size}`}>
    {showArt && <ArtPlate art={art} height={artHeight} />}
    <div className="p1-kicker">{kickerOf(dev)}</div>
    <h2 className="p1-hl">
      <button type="button" className="p1-hl-btn" onClick={() => onOpen && onOpen(dev)}>{str(dev.headline)}</button>
    </h2>
    {deckOf(dev) && <p className="p1-body">{deckOf(dev)}</p>}
    <button type="button" className="p1-cont" onClick={() => onOpen && onOpen(dev)}>Continued →</button>
  </article>
);

// Coerce a field that may be a string OR an object into display text (never
// "[object Object]"). Unknown-shaped objects render as nothing.
const richText = (v) => {
  if (v == null) return "";
  if (typeof v === "string") return v.trim();
  if (typeof v === "object") {
    if (typeof v.text === "string") return v.text.trim();
    const cand = v.summary || v.update || v.detail || v.narrative || v.description || v.status;
    return typeof cand === "string" ? cand.trim() : "";
  }
  return String(v);
};
// A geographic_reference.primary_location may be an object or a bare string.
const placeName = (loc) => {
  if (!loc) return "";
  if (typeof loc === "string") return loc.trim();
  return str(loc.name || loc.region || loc.country || loc.location || "");
};

// Trim a phrase to a scannable bullet (first N words, ellipsis if clipped).
const shortLine = (s, maxWords = 11) => {
  const words = str(s).trim().split(/\s+/).filter(Boolean);
  return words.length > maxWords ? words.slice(0, maxWords).join(" ") + "…" : words.join(" ");
};
// forward_pe (0–1 or 0–100) → a plain-language likelihood lead-in (ICD-203 style).
const likelihood = (p) => {
  if (p == null) return "";
  const pct = Number(p) <= 1 ? Number(p) * 100 : Number(p);
  if (isNaN(pct)) return "";
  if (pct >= 90) return "Near-certain";
  if (pct >= 75) return "Highly likely";
  if (pct >= 60) return "Likely";
  if (pct >= 45) return "Even odds";
  if (pct >= 30) return "Unlikely";
  return "Low odds";
};

// Split a long field into paragraphs on blank lines / sentence-dense breaks.
const toParas = (v) => {
  const t = str(v).trim();
  if (!t) return [];
  const byBlank = t.split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean);
  return byBlank.length > 1 ? byBlank : [t];
};

// ── Page 2: the lead, continued — remaining Key Takeaway paragraphs + the two
// operational figures (map, critical-technologies flow). ─────────────────────
const Page2 = ({ headline, paras, points, threatColor, mapFallback, briefingId, onBack, entitled = true }) => (
  <div className="bi-p1 p1-subpage">
    <button type="button" className="p1-back" onClick={onBack}>◂ Return to Page 1</button>
    <div className="p1-topline">
      <span>The Global Situation Report</span>
      <span className="p1-sep">|</span>
      <span>Page 2</span>
    </div>
    <div className="p1-banner"><span className="p1-rule" /><span className="p1-banner-t">The Lead, Continued</span><span className="p1-rule" /></div>
    <div className="p1-hr-thick" />
    <h1 className="p1-lead-hl p1-page2-hl">{headline}</h1>
    <div className="p1-byline p1-center">Continued from Page 1</div>
    {paras.length > 0 && (
      <div className="p1-lead-body p1-page2-body">
        {(entitled ? paras : paras.slice(0, 1)).map((p, i) => <p key={i} className={i === 0 ? "p1-drop" : ""}>{p}</p>)}
      </div>
    )}
    {entitled ? (
      <>
        {points && points.length > 0 && (
          <figure className="p1-figure">
            <OperationalMap points={points} threatColor={threatColor} fallbackSvgUrl={mapFallback} />
            <figcaption className="p1-figcap">The day's operational picture — reported developments plotted to terrain.</figcaption>
          </figure>
        )}
        {briefingId && (
          <figure className="p1-figure">
            <EngravingImage src={`${API_URL}/api/briefings/${briefingId}/logistics-sankey.svg`} alt="Critical-technologies exposure derived from the day's Key Developments" />
            <figcaption className="p1-figcap">Critical technologies — today's materials-to-capability exposure.</figcaption>
          </figure>
        )}
      </>
    ) : (
      <PaywallGate
        heading="Continue reading on Page 2"
        blurb="The rest of the lead — the operational map and the day's critical-technologies exposure — is available to members. Page 1 and the daily podcast are always free."
      />
    )}
    <div className="p1-center" style={{ padding: "10px 0 4px" }}>
      <button type="button" className="p1-cont p1-strong" onClick={onBack}>◂ Return to Page 1</button>
    </div>
  </div>
);

// ── Full Key Development article. ─────────────────────────────────────────────
const KdArticle = ({ dev, dateStr, onBack, entitled = true }) => {
  const pe = dev.probabilistic_assessment || {};
  const geo = dev.geographic_reference || {};
  const sources = Array.isArray(dev.sources) ? dev.sources : [];
  const body = [...toParas(dev.assessment), ...toParas(dev.implications)];
  const pePct = pe.forward_pe != null ? Math.round(Number(pe.forward_pe) * (Number(pe.forward_pe) <= 1 ? 100 : 1)) : null;
  return (
    <div className="bi-p1 p1-subpage">
      <button type="button" className="p1-back" onClick={onBack}>◂ Return to Page 1</button>
      <div className="p1-topline">
        <span>The Global Situation Report</span>
        <span className="p1-sep">|</span>
        <span>{str(dev.domain)}</span>
      </div>
      <div className="p1-hr-thick" />
      <div className="p1-kicker p1-center">{kickerOf(dev)}</div>
      <h1 className="p1-lead-hl p1-article-hl">{str(dev.headline)}</h1>
      <div className="p1-byline p1-center">
        By the Editorial Board{dev.timeframe ? ` · ${str(dev.timeframe)}` : dateStr ? ` · ${dateStr}` : ""}
      </div>
      <div className="p1-hr" />
      {body.length > 0 && (
        <div className="p1-lead-body p1-article-body">
          {(entitled ? body : body.slice(0, 1)).map((p, i) => <p key={i} className={i === 0 ? "p1-drop" : ""}>{p}</p>)}
        </div>
      )}
      {!entitled && (
        <PaywallGate
          heading="Read the full assessment"
          blurb="The complete assessment — implications, the probability estimate, ground truth, and sourcing — is available to members. Page 1 and the daily podcast are always free."
        />
      )}
      {entitled && richText(dev.escalation_update) && (
        <div className="p1-inset-note">
          <span className="p1-inset-label">Latest</span>
          <p className="p1-body" style={{ margin: 0 }}>{richText(dev.escalation_update)}</p>
        </div>
      )}
      {entitled && pePct != null && (
        <div className="p1-pe">
          <div className="p1-pe-num">{pePct}<span className="p1-pe-pct">%</span></div>
          <div className="p1-pe-txt">
            <div className="p1-pe-h">Probability Estimate</div>
            {str(pe.hypothesis) && <p className="p1-pe-hyp">{str(pe.hypothesis)}</p>}
            {str(pe.pe_narrative) && <p className="p1-body" style={{ margin: "4px 0 0" }}>{str(pe.pe_narrative)}</p>}
            {(pe.base_rate != null || pe.timeframe_days) && (
              <p className="p1-pe-meta">
                {pe.base_rate != null ? `Base rate ${Math.round(Number(pe.base_rate) * (Number(pe.base_rate) <= 1 ? 100 : 1))}%` : ""}
                {pe.base_rate != null && pe.timeframe_days ? " · " : ""}
                {pe.timeframe_days ? `${pe.timeframe_days}-day horizon` : ""}
              </p>
            )}
          </div>
        </div>
      )}
      {entitled && richText(geo.strategic_significance) && (
        <div className="p1-inset-note">
          <span className="p1-inset-label">Ground truth{placeName(geo.primary_location) ? ` — ${placeName(geo.primary_location)}` : ""}</span>
          <p className="p1-body" style={{ margin: 0 }}>{richText(geo.strategic_significance)}</p>
        </div>
      )}
      {entitled && sources.length > 0 && (
        <div className="p1-sources">
          <div className="p1-sources-h">Sourcing</div>
          <ol className="p1-sources-list">
            {sources.map((s, i) => (
              <li key={i}>
                <span className="p1-src-title">{str(s.title) || str(s.url)}</span>
                {(s.reliability || s.credibility) && (
                  <span className="p1-src-grade"> · {[str(s.reliability), str(s.credibility)].filter(Boolean).join("/")}</span>
                )}
                {s.summary && <span className="p1-src-sum"> — {str(s.summary)}</span>}
              </li>
            ))}
          </ol>
        </div>
      )}
      <div className="p1-center" style={{ padding: "12px 0 4px" }}>
        <button type="button" className="p1-cont p1-strong" onClick={onBack}>◂ Return to Page 1</button>
      </div>
    </div>
  );
};

export default function FrontPage({ briefingId, masthead = {}, entitled = true }) {
  const [pub, setPub] = useState(null);
  const [err, setErr] = useState(false);
  const [heroArt, setHeroArt] = useState(null);
  const [balance, setBalance] = useState({ side: null, height: 0, art: null });
  const [sub, setSub] = useState(null); // null | "page2" | { dev }
  const leftRef = useRef(null);
  const rightRef = useRef(null);
  const balancedFor = useRef(null);

  // Reset any open sub-page (Page 2 / article) when the briefing changes.
  useEffect(() => { setSub(null); }, [briefingId]);
  // Sub-pages open at the top of the view.
  useEffect(() => { if (sub && typeof window !== "undefined") window.scrollTo(0, 0); }, [sub]);

  useEffect(() => {
    if (!briefingId) return;
    const tok = typeof localStorage !== "undefined" && localStorage.getItem("token");
    fetch(`${API_URL}/api/briefings/${briefingId}/published`, tok ? { headers: { Authorization: `Bearer ${tok}` } } : undefined)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setPub)
      .catch(() => setErr(true));
  }, [briefingId]);

  const w = (pub && pub.written) || {};
  const kt = w.key_takeaway || {};
  const paras = Array.isArray(kt.paragraphs) ? kt.paragraphs.map(str) : [];
  const headline = str(masthead.daily_theme) || str(pub && pub.daily_theme) || "Today’s Briefing";
  const deck = str(kt.thematic_context && kt.thematic_context.topic_sentence);
  const leadPara = paras[0] || deck;
  const threat = String(masthead.threat_level || kt.threat_level || "").toUpperCase();
  const kds = Array.isArray(w.key_developments) ? w.key_developments : [];
  const mps = Array.isArray(w.monitoring_priorities) ? w.monitoring_priorities.slice(0, 3) : [];

  const d = masthead.date ? new Date(masthead.date) : new Date();
  const dateStr = d.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric", timeZone: "UTC" });

  const fIdx = Math.max(0, kds.findIndex(isCritical));
  const feature = kds[fIdx];
  const others = kds.filter((_, i) => i !== fIdx);
  const left = others.filter((_, i) => i % 2 === 0);
  const rightRest = others.filter((_, i) => i % 2 === 1);

  const threatColor = (lvl) => {
    const s = String(lvl || "").toUpperCase();
    if (s.startsWith("CRIT")) return "#A63A2B";
    if (s.startsWith("HIGH")) return "#C8963E";
    if (s.startsWith("MOD") || s.startsWith("MED")) return "#8A6D3B";
    return "#5A5D60";
  };
  const mapPoints = kds.map((dv, i) => {
    const loc = dv && dv.geographic_reference && dv.geographic_reference.primary_location;
    return loc && loc.latitude != null && loc.longitude != null
      ? { lat: Number(loc.latitude), lng: Number(loc.longitude), kd: i + 1,
          label: str(dv.map_label) || loc.name || loc.region || loc.country || str(dv.region) || "", severity: dv.severity }
      : null;
  }).filter(Boolean);
  // "At a glance" — the day's top developments as forward-looking, hedged
  // probability lines (from each KD's PE hypothesis), not restated headlines.
  const leadBullets = kds.slice(0, 3).map((dv) => {
    const pe = dv.probabilistic_assessment || {};
    const hyp = richText(pe.hypothesis);
    if (hyp) return { lead: likelihood(pe.forward_pe), text: shortLine(hyp, 15) };
    const impl = firstSentence(dv.implications);
    return impl ? { lead: "", text: shortLine(impl, 16) } : null;
  }).filter(Boolean);

  // Hero art (Unsplash → engrave), keyed off the day's theme.
  useEffect(() => {
    if (!pub) return undefined;
    let alive = true;
    fetchArt(headline).then((a) => { if (alive) setHeroArt(a); });
    return () => { alive = false; };
  }, [pub, headline]);

  // Column balancer: measure the two side columns' content; drop a sized engraving
  // into the shorter one to even the page. One-shot per briefing (guarded), and the
  // plate's fixed height keeps it stable after the image loads.
  useLayoutEffect(() => {
    if (!pub || balancedFor.current === briefingId) return;
    const L = leftRef.current, R = rightRef.current;
    if (!L || !R) return;
    balancedFor.current = briefingId;
    const hL = L.offsetHeight, hR = R.offsetHeight;
    const deficit = Math.abs(hL - hR);
    if (deficit < 70) return;
    const side = hL < hR ? "left" : "right";
    const height = Math.min(Math.max(deficit - 8, 120), 260);
    setBalance({ side, height, art: null });
    const target = side === "left" ? left[0] : rightRest[0];
    if (target) fetchArt(artQuery(target)).then((a) => setBalance((b) => (b.side === side ? { ...b, art: a } : b)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pub, briefingId]);

  if (err) return <div className="bi-p1"><p className="p1-body" style={{ textAlign: "center", padding: 24 }}>Front page unavailable.</p></div>;
  if (!pub) return <div className="bi-p1"><p className="p1-body" style={{ textAlign: "center", padding: 24 }}>Setting the page…</p></div>;

  if (sub === "page2") {
    return <Page2 headline={headline} paras={paras.slice(1)} points={mapPoints} threatColor={threatColor}
      mapFallback={w.map && w.map.svg_url} briefingId={briefingId} onBack={() => setSub(null)} entitled={entitled} />;
  }
  if (sub && sub.dev) {
    return <KdArticle dev={sub.dev} dateStr={dateStr} onBack={() => setSub(null)} entitled={entitled} />;
  }

  return (
    <div className="bi-p1">
      <div className="p1-topline">
        <span>Daily open-source geopolitical intelligence</span>
        <span className="p1-sep">|</span>
        <span>Fifty Cents</span>
        <span className="p1-sep">|</span>
        <span>{dateStr} · Vol. LXXXI · No. 172</span>
      </div>
      <div className="p1-banner">
        <span className="p1-rule" />
        <span className="p1-banner-t">Global Situation Report</span>
        <span className="p1-rule" />
      </div>
      {threat && (
        <div className="p1-threat">
          <span className="p1-threat-l">Threat level</span>
          <span className="p1-threat-chip">{threat}</span>
        </div>
      )}
      <div className="p1-hr-thick" />

      {pub && pub.audio && pub.audio.audio_url && (
        <div className="p1-audio">
          <AudioPlayer audioUrl={pub.audio.audio_url} title="Daily Assessment" />
        </div>
      )}

      <div className="p1-grid">
        {/* LEFT */}
        <section className="p1-col p1-col-l">
          <div ref={leftRef}>
            {left.map((dev, i) => (
              <React.Fragment key={i}>
                {i > 0 && <div className="p1-hr" />}
                <KdStory dev={dev} size={i === 0 ? "" : "sm"} onOpen={(d) => setSub({ dev: d })}
                  showArt={balance.side === "left" && i === 0} art={balance.art} artHeight={balance.height} />
              </React.Fragment>
            ))}
          </div>
        </section>

        {/* CENTER — THE LEAD */}
        <section className="p1-col p1-col-c">
          <div className="p1-hero">
            <EngravingImage src={(heroArt && heroArt.imageUrl) || HERO_FALLBACK} alt="" />
          </div>
          <p className="p1-cap">{heroArt && heroArt.imageUrl ? <Attribution art={heroArt} /> : "The day’s operational picture."}</p>
          <div className="p1-kicker p1-center p1-crimson">The Lead</div>
          <h1 className="p1-lead-hl">{headline}</h1>
          {leadBullets.length > 0 && (
            <div className="p1-glance">
              <div className="p1-glance-h">The Day at a Glance · Strategic Outlook</div>
              <ul>{leadBullets.map((b, i) => (
                <li key={i}>{b.lead && <span className="p1-glance-lede">{b.lead} — </span>}{b.text}</li>
              ))}</ul>
            </div>
          )}
          <div className="p1-byline p1-center">By the Editorial Board</div>
          {leadPara && <p className="p1-lead-body p1-drop">{leadPara}</p>}
          <div className="p1-center" style={{ paddingTop: 4 }}><button type="button" className="p1-cont p1-strong" onClick={() => setSub("page2")}>Continued on Page 2 →</button></div>
        </section>

        {/* RIGHT */}
        <section className="p1-col p1-col-r">
          <div ref={rightRef}>
            {feature && (
              <article className="p1-feature">
                <div className="p1-kicker p1-ochre">{kickerOf(feature)}</div>
                <h2 className="p1-hl">
                  <button type="button" className="p1-hl-btn" onClick={() => setSub({ dev: feature })}>{str(feature.headline)}</button>
                </h2>
                {deckOf(feature) && <p className="p1-body">{deckOf(feature)}</p>}
                <button type="button" className="p1-cont p1-on-dark" onClick={() => setSub({ dev: feature })}>Continued →</button>
              </article>
            )}
            {rightRest.map((dev, i) => (
              <React.Fragment key={i}>
                <div className="p1-hr" />
                <KdStory dev={dev} size="sm" onOpen={(d) => setSub({ dev: d })}
                  showArt={balance.side === "right" && i === 0} art={balance.art} artHeight={balance.height} />
              </React.Fragment>
            ))}
          </div>
        </section>
      </div>

      {mps.length > 0 && (
        <>
          <div className="p1-hr-thick p1-inset" />
          <div className="p1-radar">
            <div className="p1-radar-head"><span className="p1-orule" />On the Radar<span className="p1-orule" /></div>
            <div className="p1-radar-grid">
              {mps.map((m, i) => (
                <div className="p1-radar-it" key={i}>
                  <h3 className="p1-radar-h">{str(m.headline || m.title || m.priority || m)}</h3>
                  {(m.timeframe || m.severity) && <p className="p1-radar-m">{[str(m.timeframe), str(m.severity)].filter(Boolean).join(" · ")}</p>}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
